import React, { FC } from 'react';
import { classNames } from '@fullstory/util/fmt';
import { hrefForUserCard } from '@fullstory/util/utils';
import { SessionExpanderState } from '@fullstory/routes';
import { SearchUser } from '@fullstory/proto/fs/services/alexa';
import { useLocation } from 'react-router-dom';
import { InlineLink, Typography } from '@fullstory/components';
import { dateFormat } from '@fullstory/util/date';
import { useIntl } from 'react-intl';

type SessionInfoLinksProps = {
  isOnline: boolean;
  onUserInfoClick: () => void;
  sessionLinkHref: string;
  sessionLinkState: SessionExpanderState | undefined;
  user: SearchUser;
  hideDateLink?: boolean;
  longerLabel?: boolean;
};

export const SessionInfoLinks: FC<SessionInfoLinksProps> = ({
  isOnline,
  onUserInfoClick,
  sessionLinkHref,
  sessionLinkState,
  user,
  hideDateLink,
  longerLabel,
}) => {
  const intl = useIntl();
  const location = useLocation();
  const { numSessions, numMatchingSessions } = user;
  const numServerEvents = user.numMatchingOfflineEvents;

  const sessionAndServerEventCopy = (() => {
    // sanity check
    if (user.fetchClips || (!numMatchingSessions && !numServerEvents)) {
      return '';
    }

    if (numSessions == 1) {
      return intl.formatMessage({ defaultMessage: 'New visitor!', id: 'cahcJB' });
    }
    if (numMatchingSessions) {
      if (longerLabel) {
        return intl.formatMessage(
          {
            defaultMessage:
              '{numMatchingSessions, plural, one {1 matching session} other {# matching sessions}}',
            id: 'XiqAXj',
          },
          { numMatchingSessions },
        );
      } else {
        return intl.formatMessage(
          {
            defaultMessage: '{numMatchingSessions, plural, one {1 session} other {# sessions}}',
            id: 'KznKZG',
          },
          { numMatchingSessions },
        );
      }
    }
    return '';
  })();

  const dateLinkText = isOnline
    ? intl.formatMessage({ defaultMessage: 'Online', id: 'ccU6l/' })
    : dateFormat(new Date(user.lastMatchingEventStartAt as string), {
        format: 'short',
        showTime: true,
        hideYear: true,
      });
  const dateLinkClassName = classNames('date', { online: isOnline });

  return (
    <>
      {!hideDateLink && (
        <>
          {sessionLinkHref ? (
            <InlineLink
              to={{
                pathname: sessionLinkHref,
                state: sessionLinkState,
              }}
              className={dateLinkClassName}
            >
              {dateLinkText}
            </InlineLink>
          ) : (
            <InlineLink
              className={dateLinkClassName}
              to={hrefForUserCard(location.pathname, `${user.indvId}`, location.search)}
              onClick={onUserInfoClick}
            >
              {dateLinkText}
            </InlineLink>
          )}
        </>
      )}
      {sessionAndServerEventCopy ? (
        <Typography color="neutrals-n7" variant="body3" ellipsize>
          {!hideDateLink && <span>&nbsp;&middot;&nbsp;</span>}
          <InlineLink
            to={hrefForUserCard(location.pathname, `${user.indvId}`, location.search)}
            className="numSessions"
            onClick={onUserInfoClick}
          >
            {sessionAndServerEventCopy}
          </InlineLink>
        </Typography>
      ) : null}
    </>
  );
};
